// RBAC model for TPA InsureOS.
//
// Single source of truth for the project's 19 business roles and the
// page-access matrix that drives BOTH navigation filtering (NavMenu) and route
// guarding (RbacRoute). Role strings match the project's User Roles list
// exactly so the matrix is verifiable against scope.
//
// NOTE ON THE PLATFORM ROLE: the backend currently issues only two platform
// roles — 'admin' and 'user'. We map them onto the business-role model here:
//   - 'admin' is a superuser and sees/accesses everything;
//   - 'user' maps to the Member / self-service experience.
// When the backend begins issuing one of the 19 business-role strings as the
// user's role, this model honors it automatically (no frontend change needed).
// Full per-endpoint backend enforcement of the 19 roles is tracked separately.

import { menuData } from './menuData.js';

export const ROLES = {
  CLAIMS_ADJUDICATOR: 'Claims Adjudicator',
  CLAIMS_PROCESSOR: 'Claims Processor',
  COMPLIANCE_OFFICER: 'Compliance Officer',
  CORPORATE_CLIENT: 'Corporate Client',
  CSR_CALL_CENTER: 'CSR / Call Center Agent',
  CUSTOMER_SERVICE_REP: 'Customer Service Representative',
  DENTAL_PROVIDER: 'Dental Provider',
  DENTAL_REVIEW_OFFICER: 'Dental Review Officer',
  FINANCE_USER: 'Finance User',
  FRAUD_ANALYST: 'Fraud Analyst',
  HEALTHCARE_PROVIDER: 'Healthcare Provider',
  INSURANCE_COMPANY_REP: 'Insurance Company Representative',
  MEDICAL_REVIEW_OFFICER: 'Medical Review Officer',
  MEMBER: 'Member / Policy Holder',
  OPERATIONS_MANAGER: 'Operations Manager',
  PROVIDER_RELATIONS_OFFICER: 'Provider Relations Officer',
  SYSTEM_ADMINISTRATOR: 'System Administrator',
  VETERINARY_CLINIC: 'Veterinary Clinic',
  VET_REVIEW_OFFICER: 'Veterinary/Pet Claims Review Officer',
};

export const ALL_ROLES = Object.values(ROLES);

// Backend platform role that acts as a superuser (sees/accesses everything).
export const SUPERUSER_PLATFORM_ROLE = 'admin';

const R = ROLES;

/**
 * Page-access matrix: route slug → the business roles permitted to use it.
 * Derived from each page's owning use case (the use case `userRole`) plus
 * shared-workspace roles. A slug absent from this map is treated as ungated
 * (any authenticated user).
 */
export const PAGE_ROLES = {
  // --- Administrative / operational consoles ---
  'user-management': [R.SYSTEM_ADMINISTRATOR],
  'access-control-mfa-compliance-administration': [R.SYSTEM_ADMINISTRATOR],
  'ai-llm-provider-settings': [R.SYSTEM_ADMINISTRATOR],
  'member-portal-dashboard': [R.SYSTEM_ADMINISTRATOR],
  'policy-benefits-configuration': [R.OPERATIONS_MANAGER],
  'workflow-rules-escalations-sla-configuration': [R.OPERATIONS_MANAGER],
  'notification-channels-templates-delivery-rules': [R.OPERATIONS_MANAGER, R.SYSTEM_ADMINISTRATOR],
  'operational-sla-queue-turnaround-reports': [R.OPERATIONS_MANAGER],
  'provider-portal-dashboard-eligibility': [
    R.OPERATIONS_MANAGER,
    R.HEALTHCARE_PROVIDER,
    R.PROVIDER_RELATIONS_OFFICER,
  ],
  'provider-performance-utilization-service-quality-m': [R.PROVIDER_RELATIONS_OFFICER],

  // --- Claims operations ---
  'claims-inbox-auditing-queue': [R.CLAIMS_PROCESSOR],
  'ocr-claim-intake-extraction-review': [R.CLAIMS_PROCESSOR],
  'coordination-of-benefits-review': [R.CLAIMS_PROCESSOR],
  'claims-adjudication-workspace': [R.CLAIMS_ADJUDICATOR],
  'claims-appeal-grievance-resolution': [R.CLAIMS_ADJUDICATOR],
  'fraud-waste-abuse-duplicate-claim-detection': [R.FRAUD_ANALYST],

  // --- Pre-authorization / clinical review ---
  'clinical-pre-auth-queue-review': [
    R.MEDICAL_REVIEW_OFFICER,
    R.DENTAL_REVIEW_OFFICER,
    R.VET_REVIEW_OFFICER,
  ],
  'clinical-guidelines-decision-support-review': [R.MEDICAL_REVIEW_OFFICER],
  'inpatient-stay-authorization-review': [R.MEDICAL_REVIEW_OFFICER],

  // --- Finance & payouts ---
  'finance-disbursements-tax-panel': [R.FINANCE_USER],
  'member-reimbursement-payout-execution': [R.FINANCE_USER],
  'payout-service-integration-management': [R.FINANCE_USER, R.SYSTEM_ADMINISTRATOR],
  'carrier-funds-corporate-billing-reconciliation': [R.FINANCE_USER],
  'financial-claims-paid-reconciliation-reports': [R.FINANCE_USER],

  // --- Compliance & carrier oversight ---
  'regulatory-audit-compliance-reports': [R.COMPLIANCE_OFFICER],
  'carrier-program-performance-dashboard': [R.INSURANCE_COMPANY_REP],

  // --- Customer service & communications ---
  'customer-service-desk': [R.CSR_CALL_CENTER, R.CUSTOMER_SERVICE_REP],
  'communication-gateway-contact-center-integrations': [R.CSR_CALL_CENTER, R.CUSTOMER_SERVICE_REP, R.SYSTEM_ADMINISTRATOR],

  // --- Corporate portal ---
  'corporate-hr-portal-dashboard': [R.CORPORATE_CLIENT],

  // --- Member / provider self-service ---
  'request-cashless-loa': [R.MEMBER],
  'file-reimbursement-claim': [R.MEMBER],
  'explanation-of-benefits-settlement-breakdown': [R.MEMBER],
  'provider-claims-submission': [R.HEALTHCARE_PROVIDER],
  'submit-cashless-loa-walk-in': [R.HEALTHCARE_PROVIDER],
  'inpatient-admission-gop-request': [R.HEALTHCARE_PROVIDER],
  'inpatient-hospital-billing-submission': [R.HEALTHCARE_PROVIDER],
  'submit-dental-pre-authorization': [R.DENTAL_PROVIDER],
  'submit-veterinary-pre-authorization': [R.VETERINARY_CLINIC],
};

/**
 * Resolve the effective business role(s) for a user object.
 * - superuser platform role ('admin') → all roles
 * - a real business-role string → that role
 * - legacy 'user' (or anything else) → Member / self-service
 */
export function effectiveRoles(user) {
  if (!user) return [];
  if (user.role === SUPERUSER_PLATFORM_ROLE) return ALL_ROLES;
  if (ALL_ROLES.includes(user.role)) return [user.role];
  return [ROLES.MEMBER];
}

/** Is this user a superuser (sees/accesses everything)? */
export function isSuperuser(user) {
  return user?.role === SUPERUSER_PLATFORM_ROLE;
}

/**
 * Can this user access the page at `slug`?
 * Ungated slugs (not in PAGE_ROLES) are allowed for any authenticated user.
 */
export function canAccess(user, slug) {
  if (isSuperuser(user)) return true;
  const allowed = PAGE_ROLES[slug];
  if (!allowed) return true;
  const mine = effectiveRoles(user);
  return allowed.some((role) => mine.includes(role));
}

/**
 * Per-role landing page (home) slug. Each authenticated role lands on its own
 * primary workspace instead of a hard-coded admin-gated page (which previously
 * produced a spurious 403 for non-admin users). Roles without a dedicated page
 * (Dental Provider, Veterinary Clinic) fall back to their first accessible menu
 * item via landingSlug().
 */
export const HOME_BY_ROLE = {
  [ROLES.CLAIMS_ADJUDICATOR]: 'claims-adjudication-workspace',
  [ROLES.CLAIMS_PROCESSOR]: 'claims-inbox-auditing-queue',
  [ROLES.COMPLIANCE_OFFICER]: 'regulatory-audit-compliance-reports',
  [ROLES.CORPORATE_CLIENT]: 'corporate-hr-portal-dashboard',
  [ROLES.CSR_CALL_CENTER]: 'customer-service-desk',
  [ROLES.CUSTOMER_SERVICE_REP]: 'customer-service-desk',
  [ROLES.DENTAL_PROVIDER]: 'submit-dental-pre-authorization',
  [ROLES.DENTAL_REVIEW_OFFICER]: 'clinical-pre-auth-queue-review',
  [ROLES.FINANCE_USER]: 'finance-disbursements-tax-panel',
  [ROLES.FRAUD_ANALYST]: 'fraud-waste-abuse-duplicate-claim-detection',
  [ROLES.HEALTHCARE_PROVIDER]: 'provider-portal-dashboard-eligibility',
  [ROLES.INSURANCE_COMPANY_REP]: 'carrier-program-performance-dashboard',
  [ROLES.MEDICAL_REVIEW_OFFICER]: 'clinical-pre-auth-queue-review',
  [ROLES.MEMBER]: 'explanation-of-benefits-settlement-breakdown',
  [ROLES.OPERATIONS_MANAGER]: 'policy-benefits-configuration',
  [ROLES.PROVIDER_RELATIONS_OFFICER]: 'provider-performance-utilization-service-quality-m',
  [ROLES.SYSTEM_ADMINISTRATOR]: 'member-portal-dashboard',
  [ROLES.VETERINARY_CLINIC]: 'submit-veterinary-pre-authorization',
};

/**
 * Resolve the landing slug for a user: their role's home if accessible, else
 * the first menu item they can access, else null (no provisioned page).
 * Superusers land on the System Administrator home.
 */
export function landingSlug(user) {
  if (!user) return null;
  if (isSuperuser(user)) return HOME_BY_ROLE[ROLES.SYSTEM_ADMINISTRATOR];

  for (const role of effectiveRoles(user)) {
    const home = HOME_BY_ROLE[role];
    if (home && canAccess(user, home)) return home;
  }
  for (const group of menuData.children) {
    for (const item of group.children || []) {
      if (canAccess(user, item.slug)) return item.slug;
    }
  }
  return null;
}
